import type { SensorStatus } from "@sitescene/types";

import {
	hasStructuralSetupGaps,
	type LocationSetupGap,
} from "../../utils/location/location-setup-gaps";

export type ClusterSensorStatusCounts = {
	offline: number;
	online: number;
	warning: number;
};

export type ClusterStatusSegment = {
	className: string;
	kind: "empty" | "missing" | "offline" | "online" | "warning";
	text: string;
};

/**
 * Roll up how many sensors at a location are online, warning, or offline.
 * Anything that is not warning or offline is treated as online.
 */
export const aggregateSensorStatusCounts = (
	sensors: Array<{ status: SensorStatus | string }>,
): ClusterSensorStatusCounts => {
	let offline = 0;
	let warning = 0;
	let online = 0;

	for (const sensor of sensors) {
		const status = String(sensor.status).toLowerCase();
		if (status === "offline") offline += 1;
		else if (status === "warning") warning += 1;
		else online += 1;
	}

	return { offline, online, warning };
};

/** Sensor-health line for a location cluster pill / sidebar row. */
export const buildClusterStatusSegments = ({
	missingSetup = [],
	offline,
	online,
	surface = "dark",
	warning,
}: {
	/** When installation/zone are missing, show “Missing Information” instead of “No sensors”. */
	missingSetup?: readonly LocationSetupGap[];
	/** Dark = map cluster card; light = sidebar / light backgrounds. */
	surface?: "dark" | "light";
} & ClusterSensorStatusCounts): ClusterStatusSegment[] => {
	const emptyClass = surface === "light" ? "text-tertiary" : "text-white/55";
	const onlineClass =
		surface === "light" ? "text-utility-green-600" : "text-utility-green-400";

	const total = offline + online + warning;
	if (total === 0) {
		if (hasStructuralSetupGaps(missingSetup)) {
			return [
				{
					className: emptyClass,
					kind: "missing",
					text: "Missing Information",
				},
			];
		}
		return [
			{
				className: emptyClass,
				kind: "empty",
				text: "No sensors",
			},
		];
	}

	if (offline === 0 && warning === 0) {
		return [
			{
				className: onlineClass,
				kind: "online",
				text: "All Sensors Online",
			},
		];
	}

	if (online === 0 && warning === 0) {
		return [
			{
				className: "text-error-primary",
				kind: "offline",
				text: "All Offline",
			},
		];
	}

	const segments: ClusterStatusSegment[] = [];
	if (online > 0) {
		segments.push({
			className: onlineClass,
			kind: "online",
			text: `${online} Online`,
		});
	}
	if (warning > 0) {
		segments.push({
			className: "text-warning-primary",
			kind: "warning",
			text: `${warning} Warn`,
		});
	}
	if (offline > 0) {
		segments.push({
			className: "text-error-primary",
			kind: "offline",
			text: `${offline} Offline`,
		});
	}

	return segments.map((segment, index) =>
		index < segments.length - 1
			? { ...segment, text: `${segment.text},` }
			: segment,
	);
};
